import { UploadStatus } from './uploadConfig';
import {
  COLLECTION_RESOURCE_SCHEMA,
  LANGUAGE_MODEL_RESOURCE_SCHEMA,
} from '@/@core/models/api/resourcesResponse';
import { Locale } from '@/stores/useWorkspaceStore';
import { z } from 'zod';

export type Role = 'assistant' | 'user';

export interface Collection {
  name: string;
  namespace_name: string;
}
type Attachment = {
  name: string;
  type: string;
  size: number;
  status?: UploadStatus;
  fileId?: string;
  datasetId?: string;
};

export interface Message {
  role: Role;
  content: string;
  attachments?: Attachment[];
  collections?: Collection[];
}

export interface QaChatRequest {
  history: { messages: Message[] };
  fallback_language?: Locale;
  user_id: string;
  reasoningModelOverride?: string;
}
export interface QaSourcesRequest {
  trace_id: string;
}

const SCORED_TEXT_HIGHLIGHT_SCHEMA = z.object({
  start: z.number().int().min(0),
  end: z.number().int().min(0),
  score: z.number().min(-1).max(1),
});
export type QaScoredTextHighlight = z.infer<typeof SCORED_TEXT_HIGHLIGHT_SCHEMA>;

const QA_SOURCES_SCHEMA = z.object({
  source_name: z.string(),
  source_url: z.string().optional(),
  source_text: z.string(),
  highlights: z.array(SCORED_TEXT_HIGHLIGHT_SCHEMA),
  supporting: z.boolean().optional(),
});

const QA_RESPONSE_SOURCES = z.object({
  text: z.string(),
  title: z.string(),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const QA_DOCUMENT_RESPONSE_SCHEMA = z.object({
  answer: z.string().optional(),
  trace_id: z.string(),
  sources: z.array(QA_RESPONSE_SOURCES).optional(),
  safe_output: z.boolean(),
});

export type QaDocumentResponse = z.infer<typeof QA_DOCUMENT_RESPONSE_SCHEMA>;

export const QA_CHAT_TASK_EVENT_DATA = z.object({
  state: z.string(),
  id: z.string(),
});

export const AGENTIC_CHAT_MESSAGE_INSERTED_EVENT_DATA = z.object({
  insertion_id: z.string(),
  role: z.enum(['assistant', 'user']),
});

// tool event schema
const RESOURCE_SOURCE_SCHEMA = z.object({
  type: z.literal('resource'),
  resource: z.object({
    uri: z.string().nullable().optional(),
    mimeType: z.string().nullable().optional(),
    meta: z
      .object({
        chunk: z.object().nullable().optional(),
        document: z
          .object({
            author: z.string().nullable().optional(),
            createdDate: z.string().nullable().optional(),
            dataSource: z.string().nullable().optional(),
            description: z.string().nullable().optional(),
            documentType: z.string().nullable().optional(),
            fileId: z.string().nullable().optional(),
            fileName: z.string().nullable().optional(),
            fileSize: z.number().nullable().optional(),
            mimeType: z.string().nullable().optional(),
            sourceUrl: z.string().nullable().optional(),
            stageId: z.string().nullable().optional(),
            title: z.string().nullable().optional(),
            totalPages: z.number().nullable().optional(),
            totalTokenEstimationCount: z.number().nullable().optional(),
            wordcount: z.number().nullable().optional(),
          })
          .nullable()
          .optional(),
      })
      .nullable()
      .optional(),
    text: z.string().nullable().optional(),
  }),
  annotations: z.null().optional(),
  meta: z
    .object({
      relevance_score: z.number().nullable().optional(),
      document_title: z.string().nullable().optional(),
      document_last_modified: z.date().nullable().optional(),
    })
    .nullable()
    .optional(),
});
const TEXT_SOURCE_SCHEMA = z.object({
  type: z.literal('text'),
  text: z.string(),
  annotations: z.null().optional(),
  meta: z
    .object({
      relevance_score: z.number().nullable().optional(),
      document_title: z.string().nullable().optional(),
      document_last_modified: z.date().nullable().optional(),
    })
    .nullable()
    .optional(),
});
const SOURCE_SCHEMA = z.object({
  content: z.discriminatedUnion('type', [RESOURCE_SOURCE_SCHEMA, TEXT_SOURCE_SCHEMA]),

  source_index: z.number(),
});
export const AGENTIC_CHAT_TOOL_EVENT_DATA = z.discriminatedUnion('type', [
  z.object({
    call_id: z.string(),
    name: z.string(),
    type: z.literal('begin'),
  }),
  z.object({
    call_id: z.string(),
    name: z.string(),
    type: z.literal('end'),
    content: z.union([z.string(), z.array(SOURCE_SCHEMA)]).optional(),
  }),
  z.object({
    call_id: z.string(),
    name: z.string(),
    type: z.literal('error'),
    message: z.string().optional(),
  }),
]);

// attachment resource schema
const ATTACHMENT_RESOURCE_SCHEMA = z.object({
  content: z.object({
    uri: z.string().nullable().optional(),
    mimeType: z.string().nullable().optional(),
    meta: z.object().nullable().optional(),
    text: z.string().nullable().optional(),
  }),
  source_index: z.number(),
  file_id: z.string().nullable().optional(),
});
export const AGENTIC_CHAT_RESOURCE_EVENT_DATA = z.object({
  content: z.array(z.union([ATTACHMENT_RESOURCE_SCHEMA])),
});

// message event schema
export const AGENTIC_CHAT_MESSAGE_EVENT_DATA = z.object({
  stream_id: z.string(),
  type: z.enum(['begin', 'append', 'end']),
  text: z.string().optional(),
  citation_id: z.string().nullable().optional(),
});

// reasoning event schema
export const AGENTIC_CHAT_REASONING_EVENT_DATA = z.discriminatedUnion('type', [
  z.object({
    stream_id: z.string(),
    type: z.literal('begin'),
  }),
  z.object({
    stream_id: z.string(),
    type: z.literal('append'),
    text: z.string().optional(),
  }),
  z.object({
    stream_id: z.string(),
    type: z.literal('end'),
    content: z.string().optional(),
  }),
]);

export const QA_CHAT_MESSAGE_EVENT_DATA = z.object({
  type: z.string(),
  text: z.string().optional(),
  payload: z
    .object({
      trace_id: z.string(),
      answer: z.string().nullable().optional(),
      reply: z.string().nullable().optional(),
      check_safety: z.any().nullable().optional(),
      jailbreak: z.any().nullable().optional(),
      sources: z.array(z.any()).nullable().optional(),
      metadata: z.any().nullable().optional(),
    })
    .optional(),
  sources: z.array(z.any()).optional(),
  intent_response: z
    .object({
      trace_id: z.string(),
      greeting: z.boolean().optional(),
      chitchat: z.boolean().optional(),
      question: z.boolean().optional(),
      clarification: z.boolean().optional(),
      unclear: z.boolean().optional(),
      summary: z.boolean().optional(),
    })
    .optional(),
});

export const QA_SOURCES_RESPONSE_SCHEMA = z.object({
  trace_id: z.string(),
  qa_sources: z.array(QA_SOURCES_SCHEMA).min(1),
});
export type QaSourcesResponse = z.infer<typeof QA_SOURCES_RESPONSE_SCHEMA>;

export const QA_RESOURCES_RESPONSE_SCHEMA = z.array(
  z.discriminatedUnion('type', [COLLECTION_RESOURCE_SCHEMA, LANGUAGE_MODEL_RESOURCE_SCHEMA])
);
export type QaResourcesResponse = z.infer<typeof QA_RESOURCES_RESPONSE_SCHEMA>;

const IN_CONTEXT_SOURCE_SCHEMA = z.object({
  source_index: z.number(),
  highlights: z.array(
    z.object({
      start: z.number().int().min(0),
      end: z.number().int().min(0),
      relevancy: z.number().min(0).max(1),
    })
  ),
  text: z.string(),
  source_name: z.string().nullable().optional(), // ignore this field
  source_score: z.number().nullable().optional(), // ignore this field
  source_label: z.string().nullable().optional(), // ignore this field
  source_url: z.string().nullable().optional(), // ignore this field
});
export const IN_CONTEXT_SOURCES_RESPONSE_SCHEMA = z.object({
  trace_id: z.string(),
  highlighted_sources: z.array(
    z.object({
      supporting: z.array(IN_CONTEXT_SOURCE_SCHEMA),
      conflicting: z.array(IN_CONTEXT_SOURCE_SCHEMA),
    })
  ),
});
export type InContextSourcesResponseSchema = z.infer<typeof IN_CONTEXT_SOURCES_RESPONSE_SCHEMA>;
